import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Blase } from "./Blase";
import type { SatzTeil } from "./blasenForm";
import { GeistKontext, type GeistApi, type MarkierFarbe } from "./geistKontext";
import { DAUER, bewegungReduziert, flugBogen, flugDauer, flugNeigung, kurve, kurveFuer, spiel, still } from "./bewegung";
import { darf, type Anlass } from "./darf";
import { useWelt, type Fassung } from "./einstellungen";
import { Geist } from "./Geist";
import { ankerVersatz, blaseMessen, lageOhne, platzLoesen, platzStellen, platzUebergeben, platzWachsen, type Platz } from "./geistPlatz";
import { SprecherKontext } from "./kontext";
import tokens from "./tokens-w82.json";
import { bogen2, feder } from "../buehne/feder";

type Punkt = { x: number; y: number; s: number; r: number };

const T = (p: Punkt) => `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) rotate(${p.r.toFixed(2)}deg) scale(${p.s.toFixed(3)})`;
/** Lage der Aura: wie der Flieger, ohne Neigung (D-434) */
const TS = (p: Punkt) => T({ ...p, r: 0 });
/** HW12: Rand der Blase zum Bildschirm (DESIGN-W82 „Blase unterwegs“: mindestens 12 px), Kante über der Reiterleiste
 *  (FW22 Regel 2: der Inhalt verblasst 16 px über der Pille), Luft zwischen Figur und Ziel, geschätzte Höhe der Blase vor dem
 *  ersten Zeichnen (Satz in zwei Zeilen und Fuß). */
const RAND = 12;
const KANTE = 16;
const LUFT = 2;
const BLASE_SCHAETZUNG = 132;
/** W82 Web-5h-N1 (Frage 71 = A, D-677, Akten-Form): Abstand der Blase zur Figur neben ihr (wie die Akten-Karte) und die Luft
 *  über der Blase im Platz; mit LUFT und RAND zusammen ist der Platz max(Blase, Figur) + 24. */
const NEBEN = 8;
const LUFT_PLATZ = 24 - RAND - LUFT;

/** HW12 (D-666), Akten-Form (Frage 71 = A, D-677): Höhe über der Oberkante des Ziels, die die Figur (bis LUFT über dem Ziel) und
 *  die Blase links neben ihr (unten bündig mit der Figur) brauchen: die höhere von beiden und die Luft darüber; ohne Satz nur die
 *  Figur. Der Aufrufer legt RAND dazu. `versatz`: so weit liegt das Ziel unter der Oberkante des Elements, das den Platz öffnet
 *  (Kopf der Prüfkarte); die Blase endet über dem Element, also um so viel höher als die Figur. */
function ueberZiel(fassung: Fassung, blaseH: number, versatz = 0) {
  const figur = tokens.masse.geist[fassung] * tokens.masse.geist.aufModul[fassung];
  return blaseH ? Math.max(figur, blaseH + versatz) + LUFT + LUFT_PLATZ : figur + LUFT;
}

/** Kasten der Figur zum Punkt des Fliegers: er skaliert um die Mitte seiner Figur, die g/2 · (1 − s) rechts unter dem Punkt
 *  liegt (wie `kartenPunkt`). */
function figurKasten(p: { x: number; y: number; s: number }, fassung: Fassung) {
  const g = tokens.masse.geist[fassung];
  const versatz = (g / 2) * (1 - p.s);
  const halb = (g * p.s) / 2;
  return { links: p.x + versatz - halb, unten: p.y + versatz + halb, hoehe: 2 * halb };
}

/** HW12 (D-666): das Element, das auf der Seite mit `data-geist-platz-oeffnen` (Startseite) über sich Platz öffnet: das Ziel oder
 *  die Karte, in der es steht (`data-geist-bezug`); sonst keins. W82 Web-5h-N1 Punkt 8 (H1 des R2 5h-W3b): eigener Name für die
 *  Seitenmarke, `data-geist-platz` bleibt allein der Heimplatz der Figur. */
function platzElement(ziel: Element): HTMLElement | null {
  if (!ziel.closest("[data-geist-platz-oeffnen]")) return null;
  const el = ziel.closest("[data-geist-bezug]") ?? ziel;
  return el instanceof HTMLElement ? el : null;
}


/** Feste Ebene über dem Inhalt mit dem fliegenden Geist (plattformen.md „Der Geist als eigene Ebene“). Die Ebene liegt
 *  im Dokument über den Kindern und rollt mit ihnen; Ziele sind Ecken von Elementen. Seine Aura liegt unterwegs auf einer
 *  eigenen Ebene hinter dem Inhalt und folgt ihm ohne Neigung (D-434, R11 `.flieger-schein`). */
export function GeistEbene(props: { children: ReactNode }) {
  const [welt] = useWelt();
  const wurzel = useRef<HTMLDivElement>(null);
  const flieger = useRef<HTMLDivElement>(null);
  const auraEbene = useRef<HTMLDivElement>(null);
  const auraFlug = useRef<Animation | null>(null);
  const spuren = useRef<HTMLDivElement>(null);
  const halter = useRef<HTMLDivElement>(null);
  const blase = useRef<HTMLDivElement>(null);
  const platz = useRef<HTMLElement | null>(null);
  const lage = useRef<Punkt>({ x: 0, y: 0, s: 1, r: 0 });
  const flug = useRef<Animation | null>(null);
  const uhren = useRef<ReturnType<typeof setTimeout>[]>([]);
  const weltRef = useRef(welt);
  useLayoutEffect(() => {
    weltRef.current = welt;
  }, [welt]);
  const [unterwegs, setUnterwegs] = useState(false);
  const [fliegt, setFliegt] = useState(false);
  const [satz, setSatz] = useState<{ teile: SatzTeil[]; schritt?: string; weiter?: () => void; fokus?: boolean; nr: number } | null>(null);
  const [schein, setSchein] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  // W82 Web-6a (WN-38, D-1101 Befund 2, iOS BG-1 F3): der Scheinwerfer blendet weich ein (`@starting-style`, welt.css) und aus:
  // nach dem Ende bleibt er so lange wie sein Übergang (`--pgw-dauer-antwort-scheinwerfer`, gelesen am Element) mit Deckkraft 0
  // stehen, dann fällt er weg. Ein neuer ersetzt den alten sofort (eigener Schlüssel), nie zwei sichtbare Flächen
  const [scheinNr, setScheinNr] = useState(0);
  const [scheinLetzt, setScheinLetzt] = useState(schein);
  const [scheinAus, setScheinAus] = useState<{ r: NonNullable<typeof schein>; nr: number } | null>(null);
  if (schein !== scheinLetzt) {
    setScheinLetzt(schein);
    if (!schein && scheinLetzt) setScheinAus({ r: scheinLetzt, nr: scheinNr });
    if (schein && !scheinLetzt) {
      setScheinAus(null);
      setScheinNr((n) => n + 1);
    }
  }
  const scheinAusEl = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = scheinAusEl.current;
    if (!scheinAus || !el) return;
    // reduzierte Bewegung und `?still`: ohne Übergang (welt.css), dann sofort weg
    const ms = Math.max(0, ...getComputedStyle(el).transitionDuration.split(",").map((d) => parseFloat(d) * 1000 || 0));
    const takt = setTimeout(() => setScheinAus(null), ms);
    return () => clearTimeout(takt);
  }, [scheinAus]);
  const [rand, setRand] = useState<{ teile: SatzTeil[]; nr: number } | null>(null);
  // im Akten-Auftritt trägt er das Licht der Figur auf der Karte (60 %), damit es vor dem Bildschirmrand endet
  const [aufKarte, setAufKarte] = useState(false);
  const unterwegsRef = useRef(false);
  // W82 Web-5h3 (HW6, HW9): offene Antwort und was danach kommt (Lern-Banner, Platz der Karte); `heimwaerts` ab „Danke“
  const heimwaerts = useRef(false);
  const randRef = useRef(false);
  const haltBis = useRef(0);
  const warteschlange = useRef<(() => void)[]>([]);
  // HW9: Element, an dem er gerade steht (Mitgleiten und Bildlauf, wenn sich darüber etwas ändert); HW12: Ziel der Blase.
  // `ohne`: gemessen ohne Verschiebungen (das Element des Platzes gleitet beim Öffnen, das ist keine Änderung darüber)
  const bezug = useRef<{ el: Element; schluessel: string | null; x: number; y: number; ohne?: boolean } | null>(null);
  // HW12 (D-666): Platz über dem Ziel und die Höhe der Blase, mit der er geöffnet wurde
  const zielPlatz = useRef<Platz | null>(null);
  const platzBlase = useRef(0);
  // Akten-Form: Breite der Blase neben der Figur (schmaler, wenn links nicht genug Raum ist); null: Blase wie bisher über ihr.
  // `platzEigen`: der Platz über dem Ziel gehört zu dieser Blase (sie steht in ihm); sonst wächst er mit ihr nicht
  const platzBreite = useRef<number | null>(null);
  const platzEigen = useRef(false);
  // Station „Brief“: so weit liegt das Ziel (Kopf der Karte) unter der Oberkante der Karte; die Blase endet um so viel höher
  const platzVersatz = useRef(0);
  const zielEl = useRef<Element | null>(null);
  // R1 (HW13): das Ziel trägt `data-geist-ziel`, solange er dort steht; der stille Kartenkopf tritt dann zurück (welt.css)
  const zielSetzen = (el: Element | null) => {
    for (const alt of document.querySelectorAll("[data-geist-ziel]")) alt.removeAttribute("data-geist-ziel");
    zielEl.current = el;
    el?.setAttribute("data-geist-ziel", "");
  };
  const versatz = useRef({ x: 0, y: 0 });

  const relativ = useCallback((el: Element) => {
    const w = wurzel.current?.getBoundingClientRect() ?? { left: 0, top: 0 };
    const r = el.getBoundingClientRect();
    return { x: r.left - w.left, y: r.top - w.top, w: r.width, h: r.height };
  }, []);

  const setze = useCallback((p: Punkt) => {
    lage.current = p;
    if (flieger.current) flieger.current.style.transform = T(p);
    if (auraEbene.current) auraEbene.current.style.transform = TS(p);
  }, []);

  /** Aktueller Punkt, auch mitten im Flug (Unterbrechen-Regel). */
  const jetzt = useCallback((): Punkt => {
    const el = flieger.current;
    if (!flug.current || !el) return lage.current;
    const m = new DOMMatrixReadOnly(getComputedStyle(el).transform === "none" ? undefined : getComputedStyle(el).transform);
    return { x: m.e, y: m.f, s: Math.hypot(m.a, m.b), r: (Math.atan2(m.b, m.a) * 180) / Math.PI };
  }, []);

  const uhrenWeg = () => {
    uhren.current.forEach(clearTimeout);
    uhren.current = [];
  };

  /** HW6: steht gerade eine Antwort (unterwegs bis „Danke“, Randgeist, Satz am Platz)? */
  const antwortOffen = useCallback(() => (unterwegsRef.current && !heimwaerts.current) || randRef.current || haltBis.current > performance.now(), []);
  const freigeben = useCallback(() => {
    if (antwortOffen()) return;
    const liste = warteschlange.current;
    warteschlange.current = [];
    liste.forEach((tun) => tun());
  }, [antwortOffen]);
  const nachAntwort = useCallback(
    (tun: () => void) => {
      if (antwortOffen()) warteschlange.current.push(tun);
      else tun();
    },
    [antwortOffen],
  );
  const halten = useCallback(
    (ms: number) => {
      haltBis.current = Math.max(haltBis.current, performance.now() + ms);
      setTimeout(freigeben, ms + 1);
    },
    [freigeben],
  );

  /** HW12: sichtbarer Bereich der Seite (oben 12 px unter der festen Kopfzeile, unten über der Kante der Reiterleiste, FW22
   *  Regel 2). R1 5h-N1: die Kopfzeile (`header.leiste`, sticky) zählt nicht zum Bild, sonst steht die Blase bei großer Schrift
   *  unter ihr. */
  const sichtOben = () => {
    const kopf = document.querySelector("header.leiste")?.getBoundingClientRect();
    return (kopf && kopf.height > 0 && kopf.top < 1 ? kopf.bottom : 0) + RAND;
  };
  const sichtUnten = () => {
    const leiste = document.querySelector("nav.tab-leiste")?.getBoundingClientRect();
    return (leiste && leiste.height > 0 && leiste.top < innerHeight ? leiste.top : innerHeight) - KANTE;
  };
  /** HW12: rollt so wenig wie nötig, bis das Band (Dokument-Koordinaten des Fensters: Blase bis Ziel) ganz im Bild ist; passt
   *  es nicht, steht sein oberer Rand (die Blase) oben. Ein laufender weicher Bildlauf zählt mit seinem Ziel. */
  const absicht = useRef<number | null>(null);
  const bandZeigen = useCallback((oben: number, unten: number) => {
    const grenzeOben = sichtOben();
    const aktuell = absicht.current ?? scrollY;
    const min = unten - sichtUnten();
    const max = oben - grenzeOben;
    let ziel = max < min ? max : Math.min(Math.max(aktuell, min), max);
    ziel = Math.max(0, Math.min(ziel, document.documentElement.scrollHeight - innerHeight));
    if (Math.abs(ziel - aktuell) < 1) return;
    absicht.current = ziel;
    const sofort = bewegungReduziert() || still();
    scrollTo({ top: ziel, behavior: sofort ? "instant" : "smooth" });
    if (sofort) absicht.current = null;
  }, []);

  /** Akten-Form: Breite und Höhe der Blase neben einer Figur am Punkt `x` (gemessen, bevor der Platz aufgeht); die Blase ist so
   *  breit wie ihr Maß, höchstens bis NEBEN vor der Figur und RAND vor dem linken Rand. */
  const blaseNeben = useCallback((x: number, s: number, fassung: Fassung, teile: SatzTeil[], fuss: { schritt?: string; weiter: boolean }) => {
    const ebene = wurzel.current?.querySelector<HTMLElement>(".geist-ebene");
    if (!ebene) return null;
    const raum = figurKasten({ x, y: 0, s }, fassung).links - NEBEN - RAND;
    return blaseMessen(ebene, teile, fuss, raum);
  }, []);

  /** Bricht einen laufenden Flug ab und lässt ihn am aktuellen Punkt stehen. */
  const anhalten = useCallback(() => {
    if (!flug.current) return;
    const p = jetzt();
    flug.current.cancel();
    flug.current = null;
    auraFlug.current?.cancel();
    auraFlug.current = null;
    setze(p);
    setFliegt(false);
  }, [jetzt, setze]);

  const platzPunkt = useCallback((): Punkt | null => {
    if (!platz.current) return null;
    const figur = platz.current.querySelector(".geist-figur") ?? platz.current;
    const r = relativ(figur);
    return { x: r.x + r.w / 2, y: r.y + r.h / 2, s: 1, r: 0 };
  }, [relativ]);

  /** Flug von `von` nach `nach` als Bogen mit Neigung und Lichtspur; reduziert und `?still` sofort am Ziel. */
  const fliegeZu = useCallback(
    (von: Punkt, nach: Punkt, fassung: Fassung, mitSpur: boolean, art: "flug" | "heimweg"): Promise<boolean> => {
      const el = flieger.current;
      if (!el) return Promise.resolve(false);
      const dx = nach.x - von.x;
      const dy = nach.y - von.y;
      const weg = Math.hypot(dx, dy);
      if (still() || bewegungReduziert() || weg < 4 || typeof el.animate !== "function") {
        setze(nach);
        return Promise.resolve(true);
      }
      const dauer = flugDauer(weg, fassung, art);
      // Bogen zur Seite (quadratisch), Zeit kubisch ein und aus, Neigung in Flugrichtung, lebhaft unterwegs 5 % größer
      let px = -dy / weg;
      let py = dx / weg;
      if (Math.abs(px) < 0.3) {
        if (py > 0) [px, py] = [-px, -py];
      } else if (px > 0) [px, py] = [-px, -py];
      const bogen = flugBogen(weg, fassung);
      const cx = (von.x + nach.x) / 2 + px * bogen;
      const cy = (von.y + nach.y) / 2 + py * bogen;
      const kipp = flugNeigung(dx, fassung);
      // W82 Design-Web-1 (Teil B e, Technik aus dem Entwurf d0067f92): der Weg folgt der Feder der Bühne (lebhaft ζ 0,74, ruhig kritisch gedämpft): er ist früher am Ziel,
      // schwingt entlang der Bahn ein wenig über und kommt zur Ruhe; im schnellsten Moment streckt sich sein Körper (Squash &
      // stretch, am `svg.geist` im Flieger: der Flieger behält Lage, Drehung und Maßstab, die Aura-Ebene folgt ihm genau)
      const fd = feder(fassung === "ruhig" ? 1 : 0.74, dauer * (fassung === "ruhig" ? 0.6 : 0.82));
      const gesamt = Math.round(fd.dauer);
      const bahnPunkt = bogen2([von.x, von.y], [cx, cy], [nach.x, nach.y], 0.35);
      const bilder: Keyframe[] = [{ transform: T(von), offset: 0 }];
      const streckBilder: Keyframe[] = [{ transform: "scale(1, 1)", offset: 0 }];
      const auraBilder: Keyframe[] = [{ transform: TS(von), offset: 0 }];
      const N = 40;
      for (let j = 1; j <= N; j++) {
        const t = (j / N) * gesamt;
        const u = fd(t);
        const uu = Math.min(1, Math.max(0, u));
        const [x, y] = bahnPunkt(u);
        const tempo = Math.abs(fd(t + 8) - fd(t - 8)) / 16;
        const strecken = fassung === "ruhig" ? 0 : Math.min(0.07, tempo * 60);
        const p: Punkt = {
          x,
          y,
          s: von.s + (nach.s - von.s) * uu + (fassung === "lebhaft" ? 0.05 * Math.sin(Math.PI * uu) * ((von.s + nach.s) / 2) : 0),
          r: von.r * (1 - uu) + kipp * Math.sin(Math.PI * uu),
        };
        bilder.push({ transform: T(p), offset: j / N });
        streckBilder.push({ transform: `scale(${(j === N ? 1 : 1 + strecken).toFixed(4)}, ${(j === N ? 1 : 1 - strecken * 0.7).toFixed(4)})`, offset: j / N });
        auraBilder.push({ transform: TS(p), offset: j / N });
        if (mitSpur && j % 6 === 0 && u < 0.98 && spuren.current) {
          const punkt = document.createElement("span");
          punkt.className = "lichtspur";
          punkt.setAttribute("aria-hidden", "true");
          spuren.current.appendChild(punkt);
          const sy = p.y + 22 * p.s;
          const a = punkt.animate(
            [
              { opacity: 0, transform: `translate(${p.x}px, ${sy}px) scale(.5)` },
              { opacity: 0.85, transform: `translate(${p.x}px, ${sy}px) scale(1)`, offset: 0.12 },
              { opacity: 0, transform: `translate(${p.x + 4}px, ${sy - 16}px) scale(.2)` },
            ],
            { duration: DAUER.flug.lebhaft.lichtspur, delay: t, easing: "ease-out", fill: "backwards" },
          );
          a.finished.then(() => punkt.remove(), () => punkt.remove());
        }
      }
      setFliegt(true);
      const anim = el.animate(bilder, { duration: gesamt, easing: "linear", fill: "forwards" });
      flug.current = anim;
      const koerper = fassung === "ruhig" ? null : el.querySelector<SVGElement>("svg.geist");
      const streck = koerper?.animate(streckBilder, { duration: gesamt, easing: "linear" }) ?? null;
      if (streck) anim.addEventListener("cancel", () => streck.cancel());
      // die Aura fliegt mit derselben Zeit; ein beendeter Flug (Tipp, Taste) beendet sie mit
      const auraAnim = auraEbene.current?.animate(auraBilder, { duration: gesamt, easing: "linear", fill: "forwards" }) ?? null;
      auraFlug.current = auraAnim;
      return anim.finished.then(
        () => {
          if (flug.current !== anim) return false;
          flug.current = null;
          setze(nach);
          anim.cancel();
          auraAnim?.cancel();
          if (auraFlug.current === auraAnim) auraFlug.current = null;
          setFliegt(false);
          return true;
        },
        () => false,
      );
    },
    [setze],
  );

  /** Landung: gestaucht 1,06 x 0,92 (ruhig 1,04 x 0,95), 520 (460) ms `landen`. */
  const landen = useCallback((fassung: Fassung) => {
    const sprung = flieger.current?.querySelector<HTMLElement>(".geist-sprung");
    const stauchen = fassung === "ruhig" ? "scale(1.04, .95)" : "scale(1.06, .92)";
    spiel(sprung, [{ transform: stauchen }, { transform: "none" }], { dauer: DAUER.gesten[fassung].landen, kurve: kurve("landen"), art: "lage" });
  }, []);

  const heim = useCallback(() => {
    uhrenWeg();
    if (!unterwegsRef.current) return;
    const ziel = platzPunkt();
    setSatz(null);
    setSchein(null);
    anhalten();
    bezug.current = null;
    zielSetzen(null);
    // HW12 (D-666): der Platz geht, der Inhalt rückt über die Lage nach
    zielPlatz.current = platzStellen(zielPlatz.current, null, weltRef.current.fassung);
    // HW6: mit „Danke“ (oder Rollen, Escape) ist die Antwort vorbei; was wartet, kommt jetzt
    heimwaerts.current = true;
    freigeben();
    const fertig = () => {
      unterwegsRef.current = false;
      heimwaerts.current = false;
      setUnterwegs(false);
    };
    if (!ziel) {
      fertig();
      return;
    }
    const w = weltRef.current;
    void fliegeZu(lage.current, ziel, w.fassung, false, "heimweg").then((angekommen) => {
      if (angekommen) fertig();
    });
  }, [anhalten, fliegeZu, freigeben, platzPunkt]);

  const markiere = useCallback((el: Element, farbe: MarkierFarbe = "licht") => {
    const w = weltRef.current;
    const reduziert = bewegungReduziert() || still();
    // W10: lebhaft bleibt eine angesprungene Stelle bei reduzierter Bewegung 1,4 s markiert, ruhig nicht
    if (reduziert && w.fassung === "ruhig") return;
    const dauer = reduziert ? DAUER.gesten.lebhaft.leuchtenLeise : w.fassung === "ruhig" ? DAUER.antwort.ruhig.markieren : DAUER.antwort.lebhaft.aufleuchten;
    el.setAttribute("data-pgw-markiert", farbe);
    uhren.current.push(setTimeout(() => el.removeAttribute("data-pgw-markiert"), dauer));
  }, []);

  const fliege = useCallback(
    async (ziel: Element, anlass: Anlass, teile?: SatzTeil[], opt?: { erklaert?: boolean; schritt?: string; weiter?: () => void; fokus?: boolean; bezug?: Element | null }) => {
      const w = weltRef.current;
      const frage = { fassung: w.fassung, praesenz: w.praesenz, erklaert: opt?.erklaert ?? false };
      const sofort = bewegungReduziert() || still();
      const r0 = ziel.getBoundingClientRect();
      if (!darf(anlass, frage)) {
        // die schwebende Tab-Leiste (mobil) deckt den unteren Rand: eine Zeile dahinter ist nicht im Bild (Stack-Gegenlauf W82-Web-5, 393 x 852)
        if (r0.top < 0 || r0.bottom > sichtUnten()) ziel.scrollIntoView({ block: "center", behavior: sofort ? "instant" : "smooth" });
        markiere(ziel, "licht");
        return false;
      }
      uhrenWeg();
      const start = unterwegsRef.current ? jetzt() : platzPunkt();
      anhalten();
      if (start) setze(start);
      unterwegsRef.current = true;
      heimwaerts.current = false;
      setUnterwegs(true);
      setSatz(null);
      setSchein(null);
      // HW12 (D-666): das Ziel öffnet über sich Platz für Figur und Blase, wie die Akten-Karte (nicht im Rundflug; steht die
      // Antwort an einer Karte, deren Fläche die Seite hält, bleibt deren Platz). Akten-Form (Frage 71 = A): die Blase steht
      // links neben der Figur, ihre Höhe ist gemessen, bevor der Platz aufgeht
      const pe = anlass === "rundflug" || opt?.bezug ? null : platzElement(ziel);
      let r = relativ(ziel);
      let r1 = r0;
      const s = tokens.masse.geist.aufModul[w.fassung];
      // an der rechten Ecke des Ziels, aber so weit innen, dass sein Licht (Aura) nicht über den rechten Rand ragt
      const aura = ((w.fassung === "ruhig" ? 250 : 360) * s) / 2;
      const nachX = Math.min(r.x + r.w - 40, (wurzel.current?.clientWidth ?? innerWidth) - aura - 1);
      // Akten-Form im Platz, und ebenso bei der Antwort an einer Karte, deren Fläche die Seite hält (nach „Passt“, Frage 66 = B: die
      // Blase steht neben der Figur an der Knopfreihe, unter dem Erfolg der Karte, statt über ihm)
      const neben = (pe || opt?.bezug) && teile ? blaseNeben(nachX, s, w.fassung, teile, { schritt: opt?.schritt, weiter: w.fassung === "lebhaft" && Boolean(opt?.weiter) }) : null;
      const blaseH = teile ? (neben?.hoehe ?? BLASE_SCHAETZUNG) : 0;
      // Station „Brief“: das Ziel (Kopf der Karte) liegt unter der Oberkante der Karte, die Blase endet über ihr
      let kopfVersatz = 0;
      platzBreite.current = neben?.breite ?? null;
      platzEigen.current = Boolean(pe);
      if (!(opt?.bezug && zielPlatz.current?.el.contains(opt.bezug))) {
        // die Verankerung des Browsers ist aus, bevor sich der Platz öffnet (sonst rollte er die Seite mit)
        if (pe) document.documentElement.setAttribute("data-geist-haelt", "");
        const bedarf = (oben: number, rand: number) => {
          kopfVersatz = neben ? Math.max(0, ziel.getBoundingClientRect().top - oben) : 0;
          return ueberZiel(w.fassung, blaseH, kopfVersatz) + RAND - (ziel.getBoundingClientRect().top - (oben - rand));
        };
        zielPlatz.current = platzStellen(zielPlatz.current, pe ? { el: pe, bedarf } : null, w.fassung, () => {
          r = relativ(ziel);
          r1 = ziel.getBoundingClientRect();
        });
        platzBlase.current = blaseH;
        platzVersatz.current = kopfVersatz;
      }
      // HW12: er steht über dem Ziel (seine Figur endet an dessen Oberkante), im Platz, den es geöffnet hat (D-666)
      // (der Flieger skaliert um die Mitte seiner Figur, die um -50 % verschoben sitzt: ihre Mitte liegt g/2 · (1 - s) unter dem
      // Punkt, wie `kartenPunkt`)
      const g = tokens.masse.geist[w.fassung];
      const halb = (g * s) / 2;
      const nach: Punkt = { x: nachX, y: r.y - halb - LUFT - (g / 2) * (1 - s), s, r: 0 };
      // HW12: Ziel samt Figur und Blase (Höhe geschätzt, nach dem Zeichnen nachgeführt) ins Bild, ein Bildlauf. HW9: eine
      // Antwort steht an der Stelle des Tipps, die schon im Bild ist; dort rollt nichts (nur, wenn das Ziel außer Sicht ist),
      // außer so weit, dass ihr Platz ganz zu sehen ist
      // ein Bildlauf eines früheren Flugs ohne Satz (ohne Nachführen) zählt nicht mehr
      absicht.current = null;
      const rollen = anlass !== "flugAntwort" || r0.top < 0 || r0.top > sichtUnten();
      const ueber = zielPlatz.current ? ueberZiel(w.fassung, blaseH, kopfVersatz) : 2 * halb + LUFT + blaseH;
      if (rollen) bandZeigen(r1.top + scrollY - ueber, r1.bottom + scrollY);
      else if (zielPlatz.current) bandZeigen(r1.top + scrollY - ueber, r1.top + scrollY);
      zielSetzen(rollen ? ziel : null);
      const b = zielPlatz.current?.el ?? opt?.bezug ?? ziel;
      const ohne = b === zielPlatz.current?.el;
      const rb = ohne ? lageOhne(b) : relativ(b);
      bezug.current = { el: b, schluessel: b.getAttribute("data-geist-bezug"), x: rb.x, y: rb.y, ohne };
      versatz.current = { x: 0, y: 0 };
      const angekommen = await fliegeZu(start ?? nach, nach, w.fassung, darf("lichtspur", frage), "flug");
      if (!angekommen) return false;
      // HW9: hat sich das Ziel im Flug verschoben (Inhalt darüber), landet er an seiner neuen Stelle
      const v = versatz.current;
      if (v.x || v.y) setze({ ...nach, x: nach.x + v.x, y: nach.y + v.y });
      landen(w.fassung);
      // W82 Web-6a-N1 (WN-38, D-1157, iOS P4 `!steuerung.reduziert`): bei reduzierter Bewegung kein Scheinwerfer
      if (darf("scheinwerfer", frage) && !bewegungReduziert()) setSchein({ ...r, x: r.x + v.x, y: r.y + v.y });
      // der Fokus springt nur im Rundgang auf den nächsten Knopf; eine Antwort nimmt ihn nicht weg (F2 des Bestands)
      if (teile) setSatz((alt) => ({ teile, schritt: opt?.schritt, weiter: w.fassung === "lebhaft" ? opt?.weiter : undefined, fokus: opt?.fokus ?? Boolean(opt?.schritt), nr: (alt?.nr ?? 0) + 1 }));
      // HW15 (D-655): auch ruhig bleiben Figur und Blase zusammen am Ziel, bis „Danke“, Escape oder Rollen sie heimschicken
      return true;
    },
    [anhalten, bandZeigen, blaseNeben, fliegeZu, jetzt, landen, markiere, platzPunkt, relativ, setze],
  );

  /** Sicht-Regel (D-420): ist seine Figur im Bild, spricht er an seinem Platz (Rückgabe false, die Seite zeigt den Satz
   *  dort). Ist sie außer Sicht, kommt er so weit ins Bild, dass die Blase lesbar ist (rechts über dem bedienten Element,
   *  sonst darunter, sonst unten über der Reiterleiste), spricht 1,8 bis 3 s je nach Satzlänge und kehrt zurück:
   *  „Überall“ im Flug (ruhig ohne Lichtspur), „Nur bei Erklärungen“ als Umblenden und nur bei fälliger Erklärung,
   *  „Keine“ stumm. Auf der Startseite spricht er ruhig stattdessen als Randgeist (`rand`, darf("randgeist")). */
  const sage = useCallback(
    (teile: SatzTeil[], opt?: { erklaert?: boolean; ziel?: Element | null; rand?: boolean }) => {
      const w = weltRef.current;
      const frage = { fassung: w.fassung, praesenz: w.praesenz, erklaert: opt?.erklaert ?? false };
      if (w.praesenz === "keine") return false;
      if (unterwegsRef.current) {
        setSatz((alt) => ({ teile, schritt: alt?.schritt, nr: (alt?.nr ?? 0) + 1 }));
        return true;
      }
      const figur = platz.current?.querySelector(".geist-figur") ?? platz.current;
      const p = figur?.getBoundingClientRect();
      const sichtbar = p ? Math.max(0, Math.min(p.bottom, innerHeight) - Math.max(p.top, 0)) / Math.max(1, p.height) : 0;
      if (sichtbar >= 0.6) return false;
      if (opt?.rand && w.fassung === "ruhig") {
        if (!darf("randgeist", frage)) return false;
        // R1 (HW9): sofort als offene Antwort, nicht erst im Effekt nach dem Zeichnen; sonst gibt `halten` der Startseite den
        // Platz der Karte im selben Zug frei und sie klappt beim Nachladen zusammen
        randRef.current = true;
        setRand((alt) => ({ teile, nr: (alt?.nr ?? 0) + 1 }));
        return true;
      }
      // Überall immer, Nur bei Erklärungen nur mit fälliger Erklärung
      if (w.praesenz === "erklaeren" && !frage.erklaert) return false;
      const start = platzPunkt();
      const wz = wurzel.current;
      if (!wz) return false;
      const s = tokens.masse.geist.aufModul[w.fassung];
      const radius = ((w.fassung === "ruhig" ? 96 : 124) * s) / 2;
      // rechter Rand: weder die Figur noch ihr Licht (Aura) ragen hinaus
      const rechts = wz.clientWidth - Math.max(radius + 12, ((w.fassung === "ruhig" ? 250 : 360) * s) / 2 + 1);
      const oben = -(wz.getBoundingClientRect().top);
      const hoehe = innerHeight;
      const leiste = document.querySelector("nav.tab-leiste")?.getBoundingClientRect();
      const unten = leiste && leiste.height > 0 ? leiste.top : hoehe;
      const blaseH = 150;
      // HW12 (D-666): auf der Startseite landet er im Platz, den das Element über sich öffnet, wenn Platz und Blase von seiner
      // Oberkante an im Bild stehen (nicht an einer Karte, deren Fläche die Seite hält: dort bleibt ihr Platz)
      const pe = opt?.ziel && !opt.ziel.closest("[data-geist-bezug]") ? platzElement(opt.ziel) : null;
      const zv = opt?.ziel?.getBoundingClientRect();
      // Akten-Form (Frage 71 = A): die Blase links neben der Figur, gemessen, bevor der Platz aufgeht
      const zr = pe && opt?.ziel ? relativ(opt.ziel) : null;
      const neben = zr ? blaseNeben(Math.min(zr.x + zr.w - 40, rechts), s, w.fassung, teile, { weiter: false }) : null;
      const platzH = neben?.hoehe ?? BLASE_SCHAETZUNG;
      const imPlatz = Boolean(pe && zv && zv.top >= 0 && zv.top + ueberZiel(w.fassung, platzH) + RAND <= unten - 12);
      let z: ReturnType<typeof relativ> | null = null;
      platzBreite.current = imPlatz ? (neben?.breite ?? null) : null;
      platzEigen.current = imPlatz;
      if (imPlatz && pe && opt?.ziel) {
        const ziel = opt.ziel;
        // die Verankerung des Browsers ist aus, bevor sich der Platz öffnet (sonst rollte er die Seite mit)
        document.documentElement.setAttribute("data-geist-haelt", "");
        zielPlatz.current = platzStellen(zielPlatz.current, { el: pe, bedarf: (o, rand) => ueberZiel(w.fassung, platzH) + RAND - (ziel.getBoundingClientRect().top - (o - rand)) }, w.fassung, () => {
          z = relativ(ziel);
        });
        platzBlase.current = platzH;
        platzVersatz.current = 0;
      } else if (opt?.ziel) z = relativ(opt.ziel);
      // rechts über dem Element (im Platz), sonst darunter, sonst unten über der Reiterleiste
      let nach: Punkt;
      let amZiel = true;
      if (z && imPlatz) {
        const g = tokens.masse.geist[w.fassung];
        nach = { x: Math.min(z.x + z.w - 40, rechts), y: z.y - (g * s) / 2 - LUFT - (g / 2) * (1 - s), s, r: 0 };
      } else if (z && !pe && z.y - oben - blaseH - radius > 12) nach = { x: Math.min(z.x + z.w - 40, rechts), y: z.y - 4, s, r: 0 };
      else if (z && z.y + z.h + radius * 2 + blaseH - oben < unten - 12) nach = { x: Math.min(z.x + z.w - 40, rechts), y: z.y + z.h + radius + blaseH, s, r: 0 };
      else {
        nach = { x: rechts, y: oben + unten - radius - 12, s, r: 0 };
        amZiel = false;
      }
      uhrenWeg();
      anhalten();
      if (start) setze(start);
      unterwegsRef.current = true;
      heimwaerts.current = false;
      // R1 (HW9): steht er am bedienten Element, gleitet er mit ihm (Bezug wie nach einem Flug zum Ziel: die Karte, danach ihr
      // Platz); sonst bliebe er stehen, während eine neue Zeile darüber die Karte unter ihm verschiebt
      const b = amZiel && opt?.ziel ? (zielPlatz.current?.el ?? opt.ziel.closest("[data-geist-bezug]") ?? opt.ziel) : null;
      const ohne = Boolean(b) && b === zielPlatz.current?.el;
      const rb = b ? (ohne ? lageOhne(b) : relativ(b)) : null;
      bezug.current = b && rb ? { el: b, schluessel: b.getAttribute("data-geist-bezug"), x: rb.x, y: rb.y, ohne } : null;
      versatz.current = { x: 0, y: 0 };
      zielEl.current = null;
      setUnterwegs(true);
      setSatz(null);
      setSchein(null);
      const sprechen = () => {
        setSatz((alt) => ({ teile, nr: (alt?.nr ?? 0) + 1 }));
        const laenge = teile.reduce((a, t) => a + t.text.length, 0);
        const dauer = Math.min(3000, Math.max(1800, 1800 + (laenge - 30) * 25));
        uhren.current.push(setTimeout(heim, dauer));
      };
      if (w.praesenz === "erklaeren") {
        // Umblenden: am Platz aus, am Ziel ein (Maßstab 0,9), ohne Flug
        setze(nach);
        spiel(flieger.current, [{ opacity: 0, transform: `${T(nach)} scale(.9)` }, { opacity: 1, transform: T(nach) }], {
          dauer: DAUER.gesten.lebhaft.umsetzenEin,
          kurve: kurve("expo"),
          art: "blende",
        });
        spiel(auraEbene.current, [{ opacity: 0 }, { opacity: 1 }], { dauer: DAUER.gesten.lebhaft.umsetzenEin, kurve: kurve("expo"), art: "blende" });
        sprechen();
        return true;
      }
      void fliegeZu(start ?? nach, nach, w.fassung, darf("lichtspur", frage), "flug").then((angekommen) => {
        if (!angekommen) return;
        // R1 (HW9): hat sich das Element im Flug verschoben, landet er an seiner neuen Stelle
        const v = versatz.current;
        if (v.x || v.y) setze({ ...nach, x: nach.x + v.x, y: nach.y + v.y });
        landen(w.fassung);
        sprechen();
      });
      return true;
    },
    [anhalten, blaseNeben, fliegeZu, heim, landen, platzPunkt, relativ, setze],
  );

  // Blase unterwegs: links oberhalb des Geists, im Platz über dem Ziel links neben ihm (Akten-Form); ihre Spitze unten rechts
  // zeigt zu ihm; mindestens 12 px vom Rand
  useLayoutEffect(() => {
    const h = halter.current;
    const b = blase.current;
    const wz = wurzel.current;
    if (!satz || !h || !b || !wz) return;
    // HW12 (D-666): ist die Blase höher als geschätzt, wächst der Platz über dem Ziel; Figur, Licht und Bezug ziehen mit.
    // Akten-Form (Frage 71 = A): die Blase steht in der gemessenen Breite links neben der Figur, und der Platz folgt der höheren
    // von Blase und Figur in beide Richtungen (die Messung vor dem Öffnen trifft fast immer, dann rückt nichts)
    const p = zielPlatz.current;
    const breiteNeben = platzBreite.current;
    if (breiteNeben !== null) b.style.width = `${breiteNeben}px`;
    else b.style.removeProperty("width");
    const figurH = figurKasten(lage.current, weltRef.current.fassung).hoehe;
    const mehr = breiteNeben !== null ? Math.max(b.offsetHeight, figurH) - Math.max(platzBlase.current, figurH) : b.offsetHeight - platzBlase.current;
    if (p && platzBlase.current && (breiteNeben === null || platzEigen.current) && (mehr > 0.5 || (breiteNeben !== null && mehr < -0.5))) {
      platzBlase.current = b.offsetHeight;
      platzWachsen(p, mehr, weltRef.current.fassung);
      setze({ ...lage.current, y: lage.current.y + mehr });
      if (!still() && !bewegungReduziert()) {
        for (const el of [flieger.current, auraEbene.current]) {
          el?.animate([{ transform: `translateY(${-mehr}px)` }, { transform: "translateY(0)" }], { duration: DAUER.wechsel.sortieren[weltRef.current.fassung], easing: kurve("mit"), composite: "add" });
        }
      }
      setSchein((alt) => alt && { ...alt, y: alt.y + mehr });
      const bz = bezug.current;
      if (bz?.ohne && bz.el === p.el) bz.y = lageOhne(bz.el).y;
    }
    const g = lage.current;
    const radius = ((weltRef.current.fassung === "ruhig" ? 96 : 124) * g.s) / 2;
    const breite = b.offsetWidth;
    // Akten-Form: links neben der Figur, unten bündig mit ihr, die Spitze unten rechts waagerecht zu ihr; sonst links über ihr
    const fk = figurKasten(g, weltRef.current.fassung);
    const links = breiteNeben !== null ? Math.max(RAND, fk.links - NEBEN - breite) : Math.max(12, Math.min(g.x - radius - breite + 12, wz.clientWidth - 12 - breite));
    // (öffnet eine Karte den Platz, deren Kopf das Ziel ist, endet die Blase über der Karte, nicht in ihr)
    const oben = breiteNeben !== null ? Math.max(RAND, fk.unten - (platzEigen.current ? platzVersatz.current : 0) - b.offsetHeight) : Math.max(12, g.y - radius - b.offsetHeight + 8);
    h.style.transform = `translate(${links}px, ${oben}px)`;
    h.dataset.links = String(links);
    h.dataset.oben = String(oben);
    // HW12: Blase, Figur und Ziel ganz im Bild (die Blase ist jetzt gemessen); ohne Ziel (Sicht-Regel) wie bisher
    const z = zielEl.current?.isConnected ? zielEl.current.getBoundingClientRect() : null;
    if (z) {
      const wTop = wz.getBoundingClientRect().top + scrollY;
      // im Platz steht das Ziel, wo es nach dem Nachrücken steht: unter der Figur (die schon dort ist)
      const gr = tokens.masse.geist[weltRef.current.fassung];
      const zTop = p ? wTop + g.y + (gr * g.s) / 2 + LUFT + (gr / 2) * (1 - g.s) : z.top + scrollY;
      bandZeigen(Math.min(wTop + oben, zTop), Math.max(wTop + oben + b.offsetHeight, zTop + z.height));
    }
    absicht.current = null;
    const f = weltRef.current.fassung;
    // sie wächst aus ihrer Spitze; neben der Figur waagerecht aus ihr heraus
    const ein = breiteNeben !== null ? (f === "ruhig" ? "translateX(10px) scale(.86)" : "translateX(14px) scale(.7)") : f === "ruhig" ? "translate(14px, 10px) scale(.86)" : "translate(18px, 12px) scale(.7)";
    spiel(b, [{ opacity: 0, transform: ein }, { opacity: 1, transform: "none" }], {
      dauer: DAUER.blasen[f].unterwegsEin,
      kurve: kurve(kurveFuer("blase", f)),
      art: "lage",
    });
    if (satz.fokus) b.querySelector<HTMLButtonElement>("button[data-fokus]")?.focus({ preventScroll: true });
  }, [satz, bandZeigen, setze]);

  // Randgeist (ruhig): 4 s sichtbar, ein 320 / 600, weg sobald sein Platz wieder zu 60 % sichtbar ist
  const randEl = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!rand) return;
    spiel(randEl.current, [{ opacity: 0 }, { opacity: 1 }], { dauer: 320, kurve: kurve("expo"), art: "blende" });
    spiel(randEl.current?.firstElementChild as HTMLElement | null, [{ transform: "translateY(-12px)" }, { transform: "none" }], { dauer: 600, kurve: kurve("expo"), art: "lage" });
  }, [rand]);
  useEffect(() => {
    randRef.current = rand !== null;
    if (!rand) {
      freigeben();
      return;
    }
    const uhr = setTimeout(() => setRand(null), DAUER.blasen.ruhig.randgeistSichtbar);
    return () => clearTimeout(uhr);
  }, [rand, freigeben]);

  // HW9 (Mitgleiten, DESIGN-W82 „Flug“): ändert sich über seinem Ziel etwas (eine neue Frist im Modul, der Kopf nach dem
  // Nachladen), gleitet er mit dem Ziel und der Bildlauf gleicht aus, so bleibt die Stelle des Tipps im Bild stehen. Ein
  // ersetztes Ziel (Platzhalter der Karte) findet er über `data-geist-bezug` wieder; das eigene Rollen des Nutzers ändert
  // nichts (gemessen wird die Lage im Dokument, nicht im Fenster)
  useEffect(() => {
    if (!unterwegs) return;
    let takt = 0;
    // b-B4: zuletzt auf den Erfolg des Platzes gesetzter Scheinwerfer (undefined: noch nie, null: aus)
    let aufErfolg: { x: number; y: number; w: number; h: number } | null | undefined;
    const wurzelEl = document.documentElement;
    wurzelEl.setAttribute("data-geist-haelt", "");
    const pruefen = () => {
      const b = bezug.current;
      // HW12 (D-666): geht das Element des Platzes (abgehakte Frist), bleibt der Platz an seiner Stelle und der Bezug mit ihm;
      // der Platzhalter einer Karte findet sich wie bisher über den Bezug
      const p = zielPlatz.current;
      // R2: was im selben Einhängen darüber wuchs (der Kopfsatz nach dem Abhaken), zeigt der Anker vor dem Platz; der Bezug
      // übernimmt die neue Stelle ohne diesen Versatz, so gleicht das Mitgleiten ihn gleich danach aus (Figur und Bildlauf)
      const um = p ? ankerVersatz(p) : 0;
      if (p && !p.el.isConnected) {
        const neu = platzUebergeben(p, weltRef.current.fassung);
        zielPlatz.current = neu;
        if (b && neu && b.el === p.el && !(p.schluessel && neu.schluessel === p.schluessel)) {
          const l = lageOhne(neu.el);
          Object.assign(b, { el: neu.el, schluessel: neu.schluessel, x: l.x, y: l.y - um, ohne: true });
        }
      }
      if (b) {
        if (!b.el.isConnected && b.schluessel) b.el = document.querySelector(`[data-geist-bezug="${CSS.escape(b.schluessel)}"]`) ?? b.el;
        if (b.el.isConnected) {
          const r = b.ohne ? lageOhne(b.el) : relativ(b.el);
          const dx = r.x - b.x;
          const dy = r.y - b.y;
          if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
            b.x = r.x;
            b.y = r.y;
            if (flug.current) versatz.current = { x: versatz.current.x + dx, y: versatz.current.y + dy };
            else {
              setze({ ...lage.current, x: lage.current.x + dx, y: lage.current.y + dy });
              const h = halter.current;
              if (h) {
                const l = Number(h.dataset.links ?? 0) + dx;
                const o = Number(h.dataset.oben ?? 0) + dy;
                h.dataset.links = String(l);
                h.dataset.oben = String(o);
                h.style.transform = `translate(${l}px, ${o}px)`;
              }
              setSchein((alt) => alt && { ...alt, x: alt.x + dx, y: alt.y + dy });
            }
            if (dy) scrollBy({ top: dy, behavior: "instant" });
          }
          // W82 Web-5h-N2b (b-B4, D-700): ist der Bezug der Platz der Karte, liegt der Scheinwerfer auf dessen Erfolg (ohne Erfolg aus),
          // nicht auf der leeren Fläche der Knopfreihe; je Takt nachgeführt, denn der Platz baut sich nach dem Wechsel noch um
          if (b.el.classList.contains("karte-platz")) {
            const e = b.el.querySelector(".erfolg");
            const z = e ? relativ(e) : null;
            // nur bei einer Änderung setzen (kein Zeichnen je Takt)
            const neu = !z || !aufErfolg || Math.abs(aufErfolg.x - z.x) + Math.abs(aufErfolg.y - z.y) + Math.abs(aufErfolg.w - z.w) + Math.abs(aufErfolg.h - z.h) > 1;
            if (neu && (z || aufErfolg !== null)) setSchein((alt) => alt && z);
            aufErfolg = z;
          }
        }
      }
    };
    const schritt = () => {
      pruefen();
      takt = requestAnimationFrame(schritt);
    };
    takt = requestAnimationFrame(schritt);
    // nach dem Layout, vor dem Zeichnen: eine neue Zeile darüber wird im selben Bild ausgeglichen
    const groesse = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(pruefen);
    if (wurzel.current) groesse?.observe(wurzel.current);
    // und gleich nach jedem Einhängen von React (Mikroaufgabe nach dem Eintrag), bevor der Browser zeichnet
    const aenderung = typeof MutationObserver === "undefined" ? null : new MutationObserver(pruefen);
    if (wurzel.current) aenderung?.observe(wurzel.current, { childList: true, subtree: true, characterData: true });
    return () => {
      cancelAnimationFrame(takt);
      groesse?.disconnect();
      aenderung?.disconnect();
      wurzelEl.removeAttribute("data-geist-haelt");
    };
  }, [unterwegs, relativ, setze]);

  // Heimkehr, sobald sein Platz wieder zu 70 % (ruhig 60 %) sichtbar ist; nur beim Wiederauftauchen
  useEffect(() => {
    const el = platz.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const schwelle = welt.fassung === "ruhig" ? 0.6 : 0.7;
    let vorher = 1;
    const beobachter = new IntersectionObserver(
      ([e]) => {
        const jetztAnteil = e?.intersectionRatio ?? 0;
        if (vorher < schwelle && jetztAnteil >= schwelle) {
          if (unterwegsRef.current) heim();
          setRand(null);
        }
        vorher = jetztAnteil;
      },
      { threshold: [0, 0.3, 0.6, 0.7, 1] },
    );
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [heim, welt.fassung, unterwegs]);

  // Unterbrechen-Regel: ein Tipp oder eine Taste beendet den Flug im Endzustand; Escape schickt ihn heim
  useEffect(() => {
    if (!unterwegs) return;
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") heim();
      else flug.current?.finish();
    };
    const tipp = () => flug.current?.finish();
    window.addEventListener("keydown", taste);
    window.addEventListener("pointerdown", tipp, true);
    return () => {
      window.removeEventListener("keydown", taste);
      window.removeEventListener("pointerdown", tipp, true);
    };
  }, [unterwegs, heim]);

  // Einstellung gewechselt (auch in einem anderen Fenster): Bewegung endet im Endzustand; ohne Erklärungen heim
  useEffect(() => {
    flug.current?.finish();
    if (welt.praesenz === "keine") heim();
  }, [welt.fassung, welt.praesenz, welt.palette, heim]);

  useEffect(
    () => () => {
      uhrenWeg();
      platzLoesen(zielPlatz.current);
      // HW6: verlässt man die Seite mitten in der Antwort, geht nichts verloren (das Lern-Banner kommt dann gleich)
      const liste = warteschlange.current;
      warteschlange.current = [];
      liste.forEach((tun) => tun());
    },
    [],
  );

  const platzMelden = useCallback((el: HTMLElement | null) => {
    platz.current = el;
  }, []);

  /** Stelle der Figur über einer Akten-Karte (Vorlage `aktenAnker` „karte“, Figur 54: Mitte 40 px vor der rechten Kante,
   *  12 px über der Karte), im Maßstab des Fliegers; ohne das Einschweben der Karte (`translate`), also ihr Platz danach.
   *  Der Flieger ist so groß wie seine Figur (g) und skaliert um seine Mitte, die Figur sitzt um -50 % verschoben: ihre Mitte
   *  liegt darum um g/2 · (1 - s) neben dem Punkt. */
  const kartenPunkt = useCallback(
    (karte: Element): Punkt => {
      const r = relativ(karte);
      const [tx = 0, ty = 0] = getComputedStyle(karte)
        .translate.split(" ")
        .map((v) => parseFloat(v) || 0);
      const g = tokens.masse.geist[weltRef.current.fassung];
      const s = 54 / g;
      const versatz = (g / 2) * (1 - s);
      return { x: r.x + r.w - 40 - tx - versatz, y: r.y - 12 - ty - versatz, s, r: 0 };
    },
    [relativ],
  );

  /** Akten-Auftritt (Vorlage `aktenAuftritt`, D-420): er erscheint auf der Karte (`zeige`), fliegt zur nächsten Karte mit
   *  Lichtspur nach darf("lichtspur") (`fliege`, unterwegs ohne Satz), hüpft dort (`hopse`) und gibt die Figur an die Karte
   *  ab (`uebergeben`), sobald die Seite sie dort einsetzt. Ein Tipp beendet den Flug am Ziel, Escape bricht ihn ab (false). */
  const auftritt = useMemo<GeistApi["auftritt"]>(
    () => ({
      zeige: (karte) => {
        uhrenWeg();
        anhalten();
        const p = kartenPunkt(karte);
        setze(p);
        unterwegsRef.current = true;
        setUnterwegs(true);
        setAufKarte(true);
        setSatz(null);
        setSchein(null);
        // wie die Vorlage `.ansicht.auftritt .flieger .geist` (erscheinen 1100 expo, 120 ms später)
        spiel(flieger.current, [{ opacity: 0, transform: `${T(p)} scale(.86)` }, { opacity: 1, offset: 0.45 }, { opacity: 1, transform: T(p) }], {
          dauer: 1100,
          kurve: kurve("expo"),
          art: "blende",
          verzug: 120,
        });
        spiel(auraEbene.current, [{ opacity: 0 }, { opacity: 1 }], { dauer: 1100, kurve: kurve("expo"), art: "blende", verzug: 120 });
      },
      fliege: (karte) => {
        const w = weltRef.current;
        const frage = { fassung: w.fassung, praesenz: w.praesenz, erklaert: false };
        const start = jetzt();
        anhalten();
        // sein Erscheinen endet mit dem Abflug, sonst hielte es ihn am alten Ort
        for (const a of flieger.current?.getAnimations() ?? []) a.finish();
        return fliegeZu(start, kartenPunkt(karte), w.fassung, darf("lichtspur", frage), "flug");
      },
      hopse: () => {
        const sprung = flieger.current?.querySelector<HTMLElement>(".geist-sprung");
        spiel(sprung, [{ transform: "none" }, { transform: "translateY(-16px) scale(1.04)", offset: 0.35 }, { transform: "none" }], {
          dauer: DAUER.gesten.lebhaft.hopsen,
          kurve: kurve("feder"),
          art: "lage",
        });
      },
      uebergeben: () => {
        uhrenWeg();
        anhalten();
        unterwegsRef.current = false;
        setUnterwegs(false);
        setAufKarte(false);
      },
    }),
    [anhalten, fliegeZu, jetzt, kartenPunkt, setze],
  );

  const api = useMemo<GeistApi>(
    () => ({ fliege, heim, markiere, sage, unterwegs, nachAntwort, halten, platzMelden, auftritt }),
    [fliege, heim, markiere, sage, unterwegs, nachAntwort, halten, platzMelden, auftritt],
  );

  return (
    <GeistKontext.Provider value={api}>
      <div ref={wurzel} className="geist-ebene-wurzel">
        {/* vor dem Inhalt im Dokument: die Aura liegt hinter Glas und Text */}
        <div className="geist-schein-ebene" aria-hidden="true">
          <div ref={auraEbene} className="flieger-schein" hidden={!unterwegs} data-karte={aufKarte ? "" : undefined} data-testid="aura-ebene">
            <span className="geist-aura" />
          </div>
        </div>
        {props.children}
        <div className="geist-ebene" data-geist-ebene="">
          {schein ? (
            <span key={scheinNr} className="scheinwerfer" aria-hidden="true" style={{ left: schein.x, top: schein.y, width: schein.w, height: schein.h }} />
          ) : (
            scheinAus && (
              <span
                key={scheinAus.nr}
                ref={scheinAusEl}
                className="scheinwerfer"
                data-aus=""
                aria-hidden="true"
                style={{ left: scheinAus.r.x, top: scheinAus.r.y, width: scheinAus.r.w, height: scheinAus.r.h }}
              />
            )
          )}
          <div ref={spuren} aria-hidden="true" />
          <div ref={flieger} className="flieger" hidden={!unterwegs} aria-hidden="true">
            <Geist zustand={fliegt ? "flug" : "ruhe"} />
          </div>
          {unterwegs && satz && (
            <div ref={halter} className="unterwegs-halter" data-testid="blase-unterwegs">
              <SprecherKontext.Provider value="ebene">
                <Blase ref={blase} form="unterwegs" satz={satz.teile} label="Der Briefgeist zeigt">
                  <div className="blase-fuss">
                    {satz.schritt && <span className="blase-schritt w-zustand">{satz.schritt}</span>}
                    {/* die Knöpfe bleiben beisammen; bricht der Fuß um (schmale Blase), gehen sie gemeinsam in die zweite Zeile */}
                    <span className="blase-fuss-knoepfe">
                      <button type="button" className="knopf" data-art="sekundaer" data-klein="" data-fokus={satz.weiter ? undefined : ""} onClick={heim}>
                        Danke
                      </button>
                      {satz.weiter && (
                        <button type="button" className="knopf" data-art="primaer" data-klein="" data-fokus="" onClick={satz.weiter}>
                          Weiter
                        </button>
                      )}
                    </span>
                  </div>
                </Blase>
              </SprecherKontext.Provider>
            </div>
          )}
        </div>
        {rand && (
          <div ref={randEl} className="randgeist" data-geist-ebene="">
            <SprecherKontext.Provider value="rand">
              <span className="geist-platz-rand" aria-hidden="true">
                <Geist groesse={44} zustand="rand" />
              </span>
              <Blase form="rand" satz={rand.teile} />
            </SprecherKontext.Provider>
          </div>
        )}
      </div>
    </GeistKontext.Provider>
  );
}
